"use client";

import React, { createContext, useContext, useState, useMemo } from "react";
import { useAddresses, Address } from "@/hooks/useAddresses";
import { accountAxiosInstance } from "@/services/axios";
import { useCheckout } from "@/contexts/checkout";
import { useQueryClient } from "@tanstack/react-query";
interface AddressContextType {
  setSelectedLocationManually: (address: Address | null) => Promise<{
    success: boolean;
    error?: string;
  }>;
  addresses: Address[];
  selectedLocation: Address | null;
  addNewAddress: (address: Omit<Address, "id" | "user">) => Promise<Address>;
  deleteAddress: (addressId: number) => Promise<void>;
  updateAddress: (addressId: number, addressData: Omit<Address, "id" | "user">) => Promise<Address>;
  clearCheckoutAddress: () => Promise<void>;
  refetch: () => Promise<any>;
  isLoading: boolean;
  isSelectingAddress: boolean;
  hasFetchedAddresses: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  totalCount: number;
}
const AddressContext = createContext<AddressContextType | undefined>(undefined);
export const AddressProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [isSelectingAddress, setIsSelectingAddress] = useState(false);
  const queryClient = useQueryClient();

  // Use the new hook for address fetching
  const {
    addresses,
    totalCount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch
  } = useAddresses();
  const {
    checkoutAddress,
    updateCheckoutAddress,
    checkoutId
  } = useCheckout();

  // Manually set selected location (for when user selects from popup)
  const setSelectedLocationManually = React.useCallback(async (address: Address | null) => {
    // If no address is selected, return success immediately
    if (!address) {
      return {
        success: true
      };
    }

    // Set loading state
    setIsSelectingAddress(true);
    try {
      // Try to update the checkout system
      await updateCheckoutAddress(address);
      return {
        success: true
      };
    } catch (error: any) {
      console.error("Error setting address in checkout:", error);

      // Provide more specific error messages based on the error type
      let errorMessage = "Failed to set address in checkout.";
      if (error?.response?.status === 400) {
        errorMessage = "Invalid address data. Please check your address details.";
      } else if (error?.response?.status === 401) {
        errorMessage = "Authentication required. Please sign in again.";
      } else if (error?.response?.status === 403) {
        errorMessage = "You don't have permission to update this address.";
      } else if (error?.response?.status === 404) {
        errorMessage = "Address not found. Please try selecting a different address.";
      } else if (error?.response?.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      // Clear loading state
      setIsSelectingAddress(false);
    }
  }, [updateCheckoutAddress]);

  // Add new address
  const addNewAddress = React.useCallback(async (address_0: Omit<Address, "id" | "user">) => {
    try {
      // Use the address data directly since we now have the correct API field names
      const transformedAddress = {
        ...address_0,
        pincode: address_0.pincode || "" // Include pincode field
      };
      const {
        data
      } = await accountAxiosInstance.post("/user-addresses/", transformedAddress);

      // Invalidate (not just refetch) so all pages are cleared and re-fetched
      // fresh — avoids duplicate entries from stale paginated cache overlap
      await queryClient.invalidateQueries({
        queryKey: ["addresses"]
      });
      return data;
    } catch (error_0) {
      console.error("Error adding new address:", error_0);
      throw error_0;
    }
  }, [queryClient]);

  // Delete address
  const deleteAddress = React.useCallback(async (addressId: number) => {
    try {
      const response = await accountAxiosInstance.delete(`/user-addresses/${addressId}/`);

      // Check if the response is successful (2xx status codes including 204)
      if (response.status === 204) {
        // If deleted address was selected, clear selection
        if (checkoutAddress?.id === addressId) {
          await updateCheckoutAddress(null);
        }

        // Invalidate so all pages are cleared and re-fetched fresh
        await queryClient.invalidateQueries({
          queryKey: ["addresses"]
        });
      } else {
        // If status is not in 2xx range, throw an error
        throw new Error(`Delete failed with status: ${response.status}`);
      }
    } catch (error_1: any) {
      console.error("❌ Error deleting address:", error_1);
      throw error_1;
    }
  }, [checkoutAddress?.id, updateCheckoutAddress, queryClient]);

  // Update existing address
  const updateAddress = React.useCallback(async (addressId_0: number, addressData: Omit<Address, "id" | "user">) => {
    try {
      // Use the address data directly since we now have the correct API field names
      const transformedAddress_0 = {
        ...addressData,
        pincode: addressData.pincode || "" // Include pincode field
      };
      const {
        data: data_0
      } = await accountAxiosInstance.patch(`/user-addresses/${addressId_0}/`, transformedAddress_0);

      // Invalidate so all pages are cleared and re-fetched fresh
      await queryClient.invalidateQueries({
        queryKey: ["addresses"]
      });

      // If this address is currently selected, update the selectedLocation
      if (checkoutAddress?.id === addressId_0) {
        await updateCheckoutAddress(data_0);
      }
      return data_0;
    } catch (error_2) {
      console.error("Error updating address:", error_2);
      throw error_2;
    }
  }, [checkoutAddress?.id, updateCheckoutAddress, queryClient]);

  // Clear checkout address while maintaining line items
  const clearCheckoutAddress = React.useCallback(async () => {
    if (!checkoutId) {
      return;
    }
    await updateCheckoutAddress(null);
  }, [checkoutId, updateCheckoutAddress]);
  const contextValue = useMemo(() => ({
    setSelectedLocationManually,
    addresses,
    selectedLocation: checkoutAddress,
    addNewAddress,
    deleteAddress,
    updateAddress,
    clearCheckoutAddress,
    refetch,
    isLoading,
    isSelectingAddress,
    hasFetchedAddresses: addresses.length > 0,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    totalCount
  }), [addresses, checkoutAddress, isLoading, isSelectingAddress, hasNextPage, isFetchingNextPage, totalCount, setSelectedLocationManually, addNewAddress, deleteAddress, updateAddress, clearCheckoutAddress, refetch]);
  return <AddressContext.Provider value={contextValue}>
      {children}
    </AddressContext.Provider>;
};
export const useAddress = () => {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error("useAddress must be used within an AddressProvider");
  }
  return context;
};